import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import LanguageSelector, { SupportedLanguage } from "@/components/multilingual/LanguageSelector";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { defaultLanguage, LANGUAGE_COOKIE_NAME, detectBrowserLanguage } from "@/lib/i18n";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import SEOHead from "@/components/common/SEOHead";

type Step = "form" | "done";
type TerminationMode = "earliest" | "date";

/**
 * § 312k BGB Bestätigungsseite.
 *
 * BGH I ZR 200/25 (16.07.2026): the confirmation page must contain nothing
 * beyond the statutory fields and the confirmation button — no navigation,
 * no offers, no alternatives, no marketing. Do NOT add Navbar, Footer,
 * pricing links or retention copy to this page.
 */
const COPY: Record<SupportedLanguage, Record<string, string>> = {
  de: {
    title: "Verträge hier kündigen",
    typeLabel: "Art der Kündigung",
    ordinary: "Ordentliche Kündigung",
    extraordinary: "Außerordentliche Kündigung",
    reasonLabel: "Kündigungsgrund",
    timingLabel: "Zeitpunkt, zu dem die Kündigung wirksam werden soll",
    timingEarliest: "Zum nächstmöglichen Zeitpunkt",
    timingDate: "Zum folgenden Datum",
    emailLabel: "E-Mail-Adresse Ihres Kontos",
    emailHelp: "Für Ihre Identifizierung und die Übermittlung der Kündigungsbestätigung.",
    nameLabel: "Name (optional)",
    contractLabel: "Vertragsangaben, z. B. Tarif oder Rechnungsnummer (optional)",
    submit: "Jetzt kündigen",
    doneTitle: "Kündigung eingegangen",
    doneBody:
      "Ihre Kündigungserklärung ist bei uns eingegangen. Eine Bestätigung mit Datum und Uhrzeit des Eingangs sowie dem Beendigungszeitpunkt senden wir Ihnen an die angegebene E-Mail-Adresse. Bereits bezahlte Zeiträume bleiben nutzbar; die Guthabenregeln finden Sie in § 16 der Nutzungsbedingungen.",
    download: "Kündigungserklärung als Textdatei speichern",
    receiptId: "Bestätigungsnummer",
    receiptAt: "Eingang",
    feedbackTitle: "Freiwillige Rückmeldung",
    feedbackHint:
      "Ihre Kündigung ist bereits wirksam abgegeben. Wenn Sie möchten, können Sie uns freiwillig mitteilen, warum Sie gehen.",
    feedbackSend: "Rückmeldung senden",
    feedbackThanks: "Danke für Ihre Rückmeldung.",
    backHome: "Zur Startseite",
    invalidEmail: "Bitte geben Sie eine gültige E-Mail-Adresse an.",
    invalidDate: "Bitte geben Sie ein Datum an.",
    failed:
      "Die Kündigung konnte nicht übermittelt werden. Bitte versuchen Sie es erneut oder schreiben Sie an contact@paperverse.io.",
    declarationTitle: "Kündigungserklärung",
  },
  en: {
    title: "Cancel your contract here",
    typeLabel: "Type of cancellation",
    ordinary: "Ordinary cancellation",
    extraordinary: "Extraordinary cancellation",
    reasonLabel: "Reason for the extraordinary cancellation",
    timingLabel: "Date on which the cancellation should take effect",
    timingEarliest: "At the earliest possible date",
    timingDate: "On the following date",
    emailLabel: "Email address of your account",
    emailHelp: "Used to identify you and to send the cancellation confirmation.",
    nameLabel: "Name (optional)",
    contractLabel: "Contract details, e.g. plan or invoice number (optional)",
    submit: "Cancel now",
    doneTitle: "Cancellation received",
    doneBody:
      "We have received your cancellation. A confirmation with the date and time of receipt and the end date will be sent to the email address you provided. Periods already paid for remain usable; credit rules are set out in § 16 of the Terms of Service.",
    download: "Save the cancellation declaration as a text file",
    receiptId: "Confirmation ID",
    receiptAt: "Received",
    feedbackTitle: "Optional feedback",
    feedbackHint:
      "Your cancellation has already been submitted. If you like, you can optionally tell us why you are leaving.",
    feedbackSend: "Send feedback",
    feedbackThanks: "Thank you for your feedback.",
    backHome: "Back to the homepage",
    invalidEmail: "Please enter a valid email address.",
    invalidDate: "Please provide a date.",
    failed: "We could not submit your cancellation. Please try again or write to contact@paperverse.io.",
    declarationTitle: "Cancellation declaration",
  },
  fr: {
    title: "Résilier votre contrat ici",
    typeLabel: "Type de résiliation",
    ordinary: "Résiliation ordinaire",
    extraordinary: "Résiliation extraordinaire",
    reasonLabel: "Motif de la résiliation extraordinaire",
    timingLabel: "Date à laquelle la résiliation doit prendre effet",
    timingEarliest: "À la première échéance possible",
    timingDate: "À la date suivante",
    emailLabel: "Adresse e-mail de votre compte",
    emailHelp: "Sert à vous identifier et à envoyer la confirmation de résiliation.",
    nameLabel: "Nom (facultatif)",
    contractLabel: "Informations contractuelles, p. ex. formule ou n° de facture (facultatif)",
    submit: "Résilier maintenant",
    doneTitle: "Résiliation reçue",
    doneBody:
      "Nous avons bien reçu votre résiliation. Une confirmation indiquant la date et l'heure de réception ainsi que la date de fin vous sera envoyée par e-mail. Les périodes déjà payées restent utilisables ; les règles relatives aux crédits figurent au § 16 des conditions d'utilisation.",
    download: "Enregistrer la déclaration de résiliation en fichier texte",
    receiptId: "Numéro de confirmation",
    receiptAt: "Réception",
    feedbackTitle: "Retour facultatif",
    feedbackHint:
      "Votre résiliation est déjà enregistrée. Si vous le souhaitez, vous pouvez nous indiquer pourquoi vous partez.",
    feedbackSend: "Envoyer le retour",
    feedbackThanks: "Merci pour votre retour.",
    backHome: "Retour à l'accueil",
    invalidEmail: "Veuillez saisir une adresse e-mail valide.",
    invalidDate: "Veuillez indiquer une date.",
    failed: "La résiliation n'a pas pu être transmise. Réessayez ou écrivez à contact@paperverse.io.",
    declarationTitle: "Déclaration de résiliation",
  },
  es: {
    title: "Cancelar su contrato aquí",
    typeLabel: "Tipo de cancelación",
    ordinary: "Cancelación ordinaria",
    extraordinary: "Cancelación extraordinaria",
    reasonLabel: "Motivo de la cancelación extraordinaria",
    timingLabel: "Fecha en la que debe surtir efecto la cancelación",
    timingEarliest: "En la fecha más próxima posible",
    timingDate: "En la siguiente fecha",
    emailLabel: "Correo electrónico de su cuenta",
    emailHelp: "Sirve para identificarle y enviarle la confirmación de la cancelación.",
    nameLabel: "Nombre (opcional)",
    contractLabel: "Datos del contrato, p. ej. plan o número de factura (opcional)",
    submit: "Cancelar ahora",
    doneTitle: "Cancelación recibida",
    doneBody:
      "Hemos recibido su cancelación. Le enviaremos una confirmación con la fecha y hora de recepción y la fecha de finalización al correo indicado. Los periodos ya pagados siguen siendo utilizables; las reglas sobre créditos figuran en el § 16 de las condiciones de uso.",
    download: "Guardar la declaración de cancelación como archivo de texto",
    receiptId: "Número de confirmación",
    receiptAt: "Recepción",
    feedbackTitle: "Comentario voluntario",
    feedbackHint:
      "Su cancelación ya se ha presentado. Si lo desea, puede contarnos voluntariamente por qué se marcha.",
    feedbackSend: "Enviar comentario",
    feedbackThanks: "Gracias por su comentario.",
    backHome: "Volver al inicio",
    invalidEmail: "Introduzca una dirección de correo electrónico válida.",
    invalidDate: "Indique una fecha.",
    failed: "No se pudo enviar la cancelación. Inténtelo de nuevo o escriba a contact@paperverse.io.",
    declarationTitle: "Declaración de cancelación",
  },
};

const LOCALE_BY_LANGUAGE: Record<SupportedLanguage, string> = {
  de: "de-DE",
  en: "en-GB",
  fr: "fr-FR",
  es: "es-ES",
};

const CancelContract = () => {
  const [language, setLanguage] = useState<SupportedLanguage>(defaultLanguage);
  const [step, setStep] = useState<Step>("form");
  const [submitting, setSubmitting] = useState(false);
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [contractReference, setContractReference] = useState("");
  const [cancellationType, setCancellationType] = useState<"ordinary" | "extraordinary">("ordinary");
  const [terminationMode, setTerminationMode] = useState<TerminationMode>("earliest");
  const [terminationDate, setTerminationDate] = useState("");
  const [reason, setReason] = useState("");
  const [receipt, setReceipt] = useState<{ id: string; receivedAt: string } | null>(null);
  const [feedback, setFeedback] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);

  useEffect(() => {
    const saved = localStorage.getItem(LANGUAGE_COOKIE_NAME);
    if (saved && ["en", "de", "fr", "es"].includes(saved)) {
      setLanguage(saved as SupportedLanguage);
    } else {
      setLanguage(detectBrowserLanguage());
    }
  }, []);

  // Prefill for visitors who happen to be signed in. Never required:
  // the page must work without any login (OLG Nürnberg 3 U 2214/23).
  useEffect(() => {
    let active = true;
    supabase.auth.getUser().then(({ data }) => {
      if (active && data?.user?.email) setEmail((prev) => prev || data.user!.email!);
    });
    return () => {
      active = false;
    };
  }, []);

  const t = COPY[language];
  const locale = LOCALE_BY_LANGUAGE[language];

  const declarationText = useMemo(() => {
    if (!receipt) return "";
    return [
      `${t.declarationTitle} — PaperVerse`,
      "",
      `${t.receiptAt}: ${new Date(receipt.receivedAt).toLocaleString(locale)}`,
      `${t.receiptId}: ${receipt.id}`,
      `${t.typeLabel}: ${cancellationType === "ordinary" ? t.ordinary : t.extraordinary}`,
      `${t.timingLabel}: ${
        terminationMode === "earliest" ? t.timingEarliest : terminationDate
      }`,
      `${t.emailLabel}: ${email}`,
      fullName ? `${t.nameLabel}: ${fullName}` : null,
      contractReference ? `${t.contractLabel}: ${contractReference}` : null,
      cancellationType === "extraordinary" && reason ? `${t.reasonLabel}: ${reason}` : null,
    ]
      .filter(Boolean)
      .join("\n");
  }, [
    receipt,
    t,
    locale,
    cancellationType,
    terminationMode,
    terminationDate,
    email,
    fullName,
    contractReference,
    reason,
  ]);

  const handleDownload = () => {
    const blob = new Blob([declarationText], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `paperverse-cancellation-${receipt?.id ?? "declaration"}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim())) {
      toast.error(t.invalidEmail);
      return;
    }
    if (terminationMode === "date" && !terminationDate) {
      toast.error(t.invalidDate);
      return;
    }
    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke("cancel-contract", {
        body: {
          email: email.trim(),
          full_name: fullName.trim() || null,
          contract_reference: contractReference.trim() || null,
          cancellation_type: cancellationType,
          termination_mode: terminationMode,
          termination_date: terminationMode === "date" ? terminationDate : null,
          reason: cancellationType === "extraordinary" ? reason.trim() || null : null,
        },
      });
      const payload = data as { ok?: boolean; confirmation_id?: string; received_at?: string } | null;
      if (error || !payload?.ok) throw error ?? new Error("failed");
      setReceipt({
        id: payload.confirmation_id ?? "",
        receivedAt: payload.received_at ?? new Date().toISOString(),
      });
      setStep("done");
    } catch {
      toast.error(t.failed);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendFeedback = async () => {
    if (!feedback.trim() || !receipt?.id) return;
    try {
      await supabase.functions.invoke("cancel-contract", {
        body: { feedback_for: receipt.id, feedback: feedback.trim() },
      });
    } catch {
      // Feedback is voluntary and must never affect the cancellation.
    }
    setFeedbackSent(true);
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Cancel your contract | PaperVerse"
        description="Cancel your PaperVerse subscription online at any time — no login required, with immediate confirmation by email."
      />
      {/* Deliberately minimal chrome: no navigation, no offers (BGH I ZR 200/25). */}
      <header className="border-b">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <span className="font-semibold tracking-tight">PaperVerse</span>
          <LanguageSelector language={language} onChange={setLanguage} />
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-10">
        <h1 className="text-2xl font-semibold tracking-tight mb-6">{t.title}</h1>

        {step === "form" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base font-medium">{t.title}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="space-y-2">
                <Label>{t.typeLabel}</Label>
                <RadioGroup
                  value={cancellationType}
                  onValueChange={(v) => setCancellationType(v as "ordinary" | "extraordinary")}
                  className="space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="ordinary" id="cancel-ordinary" />
                    <Label htmlFor="cancel-ordinary" className="font-normal">{t.ordinary}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="extraordinary" id="cancel-extraordinary" />
                    <Label htmlFor="cancel-extraordinary" className="font-normal">{t.extraordinary}</Label>
                  </div>
                </RadioGroup>
              </div>

              {cancellationType === "extraordinary" && (
                <div className="space-y-2">
                  <Label htmlFor="cancel-reason">{t.reasonLabel}</Label>
                  <Textarea
                    id="cancel-reason"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    rows={3}
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>{t.timingLabel}</Label>
                <RadioGroup
                  value={terminationMode}
                  onValueChange={(v) => setTerminationMode(v as TerminationMode)}
                  className="space-y-2"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="earliest" id="cancel-timing-earliest" />
                    <Label htmlFor="cancel-timing-earliest" className="font-normal">{t.timingEarliest}</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="date" id="cancel-timing-date" />
                    <Label htmlFor="cancel-timing-date" className="font-normal">{t.timingDate}</Label>
                  </div>
                </RadioGroup>
                {terminationMode === "date" && (
                  <Input
                    id="cancel-termination-date"
                    type="date"
                    aria-label={t.timingDate}
                    value={terminationDate}
                    onChange={(e) => setTerminationDate(e.target.value)}
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="cancel-email">{t.emailLabel}</Label>
                <Input
                  id="cancel-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
                <p className="text-xs text-muted-foreground">{t.emailHelp}</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cancel-name">{t.nameLabel}</Label>
                <Input id="cancel-name" value={fullName} onChange={(e) => setFullName(e.target.value)} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="cancel-contract">{t.contractLabel}</Label>
                <Input
                  id="cancel-contract"
                  value={contractReference}
                  onChange={(e) => setContractReference(e.target.value)}
                />
              </div>

              <Button onClick={handleSubmit} disabled={submitting} className="w-full">
                {t.submit}
              </Button>
            </CardContent>
          </Card>
        )}

        {step === "done" && (
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium">{t.doneTitle}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-muted-foreground">{t.doneBody}</p>
                {receipt && (
                  <dl className="text-sm space-y-1">
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">{t.receiptAt}</dt>
                      <dd className="text-right">{new Date(receipt.receivedAt).toLocaleString(locale)}</dd>
                    </div>
                    <div className="flex justify-between gap-4">
                      <dt className="text-muted-foreground">{t.receiptId}</dt>
                      <dd className="text-right break-all">{receipt.id}</dd>
                    </div>
                  </dl>
                )}
                <Button variant="outline" onClick={handleDownload} className="w-full sm:w-auto">
                  {t.download}
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium">{t.feedbackTitle}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-sm text-muted-foreground">{t.feedbackHint}</p>
                {feedbackSent ? (
                  <p className="text-sm">{t.feedbackThanks}</p>
                ) : (
                  <>
                    <Textarea
                      aria-label={t.feedbackTitle}
                      value={feedback}
                      onChange={(e) => setFeedback(e.target.value)}
                      rows={3}
                    />
                    <Button
                      variant="outline"
                      onClick={handleSendFeedback}
                      disabled={!feedback.trim()}
                      className="w-full sm:w-auto"
                    >
                      {t.feedbackSend}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>

            <Link to="/" className="text-sm text-muted-foreground hover:text-foreground underline">
              {t.backHome}
            </Link>
          </div>
        )}
      </main>
    </div>
  );
};

export default CancelContract;
